import { useEffect, useState } from 'react';
import { Sparkles, Globe, Loader2, Check, Building2 } from 'lucide-react';
import { useToast } from '@powercore/ui';
import { api, type CompanyProfile, type ProfileFieldSource } from '../lib/api';

const font = { fontFamily: "'Outfit', sans-serif" };

const STAGES: Array<{ value: string; label: string }> = [
  { value: 'pre_pmf', label: 'Pre-PMF — still finding fit' },
  { value: 'early_traction', label: 'Early traction — first paying customers' },
  { value: 'scaling', label: 'Scaling — pushing toward €1M ARR' },
  { value: 'post_1m', label: '€1M+ — expanding' },
];

const SOURCE_LABEL: Record<ProfileFieldSource, string> = {
  website: 'from your site',
  founder: 'you',
  skill: 'from a skill',
};

function Provenance({ profile, field }: { profile: CompanyProfile; field: string }) {
  const meta = profile.field_meta?.[field];
  if (!meta) return null;
  return (
    <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400" style={font}>
      {SOURCE_LABEL[meta.source] || meta.source}
    </span>
  );
}

export function CompanyProfilePage() {
  const { addToast } = useToast();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local editable copy of the form fields.
  const [form, setForm] = useState<Partial<CompanyProfile>>({});

  const hydrate = (p: CompanyProfile) => {
    setProfile(p);
    setForm({
      company_name: p.company_name ?? '',
      one_liner: p.one_liner ?? '',
      icp: p.icp ?? '',
      positioning: p.positioning ?? '',
      pricing: p.pricing ?? '',
      competitors: p.competitors ?? [],
      stage: p.stage ?? '',
      primary_goal: p.primary_goal ?? '',
    });
    if (p.website_url) setUrl(p.website_url);
  };

  useEffect(() => {
    api.getProfile().then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (data) hydrate(data as CompanyProfile);
      setLoading(false);
    });
  }, []);

  const enrich = async () => {
    if (!url.trim()) return;
    setEnriching(true);
    const res = await api.enrichProfile(url.trim());
    setEnriching(false);
    if (res.error) {
      addToast({ title: 'Could not read that site', description: res.error.message, variant: 'error' });
      return;
    }
    const data = (res.data as any)?.data || res.data;
    if (data?.profile) {
      hydrate(data.profile as CompanyProfile);
      addToast({ title: 'Drafted from your site', description: 'Review and correct anything below.', variant: 'success' });
    }
  };

  const save = async () => {
    setSaving(true);
    const patch: Partial<CompanyProfile> = {
      company_name: form.company_name || null,
      one_liner: form.one_liner || null,
      icp: form.icp || null,
      positioning: form.positioning || null,
      pricing: form.pricing || null,
      competitors: form.competitors || [],
      stage: form.stage || null,
      primary_goal: form.primary_goal || null,
    };
    const res = await api.updateProfile(patch);
    setSaving(false);
    if (res.error) {
      addToast({ title: 'Save failed', description: res.error.message, variant: 'error' });
      return;
    }
    const data = (res.data as any)?.data || res.data;
    if (data) hydrate(data as CompanyProfile);
    addToast({ title: 'Saved', description: 'Your skills now run against this.', variant: 'success' });
  };

  if (loading) return <div className="py-24 text-center text-gray-400" style={font}>Loading…</div>;

  const isEmptyProfile = !profile?.one_liner && !profile?.company_name;

  const field = (
    key: keyof CompanyProfile,
    label: string,
    placeholder: string,
    textarea = false,
  ) => (
    <div>
      <label className="mb-1 flex items-center text-sm font-medium text-gray-700" style={font}>
        {label}
        {profile && <Provenance profile={profile} field={key as string} />}
      </label>
      {textarea ? (
        <textarea
          value={(form[key] as string) || ''}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={placeholder}
          rows={2}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
          style={font}
        />
      ) : (
        <input
          value={(form[key] as string) || ''}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
          style={font}
        />
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-100 via-white to-sky-100 ring-1 ring-indigo-200/70">
          <Building2 className="h-5 w-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900" style={font}>Your company</h1>
          <p className="text-sm text-gray-500" style={font}>The context every skill runs against. Set it once — it gets sharper as you use Mazo.</p>
        </div>
      </div>

      {/* L0 — paste your URL */}
      <div className="mb-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-mazo-50/60 to-white p-5">
        <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700" style={font}>
          <Sparkles className="h-4 w-4 text-mazo-600" /> {isEmptyProfile ? 'Start from your website' : 'Re-draft from your website'}
        </div>
        <p className="mb-3 text-xs text-gray-500" style={font}>
          Paste your site and we’ll draft your profile in seconds. We won’t overwrite anything you’ve confirmed.
        </p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && enrich()}
              placeholder="yourcompany.com"
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
              style={font}
            />
          </div>
          <button
            onClick={enrich}
            disabled={enriching || !url.trim()}
            className="flex items-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700 disabled:opacity-60"
            style={font}
          >
            {enriching ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading…</> : 'Draft profile'}
          </button>
        </div>
      </div>

      {/* Profile fields */}
      <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5">
        {field('company_name', 'Company name', 'Acme')}
        {field('one_liner', 'What you sell', 'One sentence — what it is and for whom', true)}
        {field('icp', 'Ideal customer (ICP)', 'Who you sell to', true)}
        {field('positioning', 'Positioning', 'How you’re different', true)}
        {field('pricing', 'Pricing', 'e.g. $X/seat/mo, usage-based, free trial')}

        <div>
          <label className="mb-1 flex items-center text-sm font-medium text-gray-700" style={font}>
            Competitors {profile && <Provenance profile={profile} field="competitors" />}
          </label>
          <input
            value={(form.competitors || []).join(', ')}
            onChange={(e) => setForm((f) => ({ ...f, competitors: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }))}
            placeholder="Comma-separated"
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
            style={font}
          />
        </div>

        {/* L1 — stage + goal */}
        <div className="grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 flex items-center text-sm font-medium text-gray-700" style={font}>
              Stage {profile && <Provenance profile={profile} field="stage" />}
            </label>
            <select
              value={form.stage || ''}
              onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
              style={font}
            >
              <option value="">Select…</option>
              {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {field('primary_goal', 'Primary goal this quarter', 'e.g. 10 paying customers')}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          {profile?.updated_at && (
            <span className="text-xs text-gray-400" style={font}>v{profile.version}</span>
          )}
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-60"
            style={font}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompanyProfilePage;
